import {
    Box,
    Grid,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Button,
    FormControl,
    InputLabel,
    Select,
    IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import SectionHeader from '../../../components/SectionHeader';
import DrawerVisitas from '../components/DrawerVisitas';
import { useEjecutionsVisits } from './hooks/useEjecutionVisits';
import { DialogNewRGRL } from '../../Presentaciones/components/DialogNewRGRL';
import { useNavigate } from 'react-router-dom';
import VerosimilitudSection from './components/VerosimilitudSection';
import RGRLSection from './components/RGRLSection';

function EjecutionsVisitsIndex() {
    const { t } = useTranslation('visitas');
    const navigate = useNavigate();

    const {
        formData,
        setFormData,
        isFormValid,
        handleChange,
        handleFalloVisitaChange,
        handleSubmit,
        handleClearSignature,
        openDrawer,
        setOpenDrawer,
        handleOpenDrawer,
        selectedVisita,
        selectedVisitaDetail,
        canvasPreventorRef,
        canvasResponsableRef,
        tiposVisitasOptions,
        subTypesVisitasOptions,
        loadingTiposVisitas,
        signatureErrors,
        confirmSubmit,
        rgrlPresentaciones,
        openDialogRGRL,
        handleOpenDialogRGRL,
        handleCloseDialogRGRL,
        handleCardClickRGRL,
        handleOpenVerosimilitud,
        loadingPresentaciones,
        shouldShowVerosimilitudRow,
        isVerosimilitudDraft,
        confirmCancelPresentation,
        allPresentationsCancelled,
        handleEditRGRL,
        hasRgrlPresentations,
        isVerosimilitudPendienteSinRespuestas,
        loadingVerosimilitud,
        staffError
    } = useEjecutionsVisits();

    return (
        <>
            <SectionHeader>
                <SectionHeader.Title>
                    {t('newVisit.detail')} : {selectedVisita.nombreEstablecimiento || '-'}
                </SectionHeader.Title>

                <SectionHeader.IconHeader
                    showIcon={false}
                    variant="outlined"
                    text={t('newVisit.establishment')}
                    onClick={handleOpenDrawer}
                    fullwidth
                />
                <Grid container sx={{ p: 2 }}>
                    <Grid item xs={12} sm={2}>
                        <Typography variant="h6">
                            <strong>{t('filters.visitType')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {selectedVisita.nombreTipoVisita || '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Typography variant="h6">
                            <strong>{t('filters.deadlineDate')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {selectedVisita.fechaLimite ? dayjs().format('DD/MM/YYYY') : '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Typography variant="h6">
                            <strong>{t('filters.visitDate')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {selectedVisita.fechaVisita ? dayjs().format('DD/MM/YYYY') : '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Typography variant="h6">
                            <strong>{t('filters.preventor')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {selectedVisita.nombrePreventor || '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                        <Typography variant="h6">
                            <strong>{t('filters.observations')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {selectedVisita.observacion.toString() || '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </SectionHeader>

            <Box
                component="div"
                justifyContent="center"
                sx={{ mt: 4, backgroundColor: 'white', p: 2 }}
            >
                <Typography variant="h3">
                    <strong>{t('newVisit.sectionSummary')}</strong>
                </Typography>

                <Grid container spacing={1} sx={{ p: 2 }}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.esFallida}
                                    onChange={handleFalloVisitaChange}
                                />
                            }
                            label={t('newVisit.label.failedVisit')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label={t('newVisit.label.totalStaff')}
                        name="totalPersonal"
                        type="number"
                        value={formData.totalPersonal ?? ''}
                        onChange={handleChange}
                        disabled={formData.esFallida}
                        error={staffError} 
                        helperText={staffError ? t('newVisit.error.totalStaffMismatch') : ''} 
                    />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label={t('newVisit.label.administrativeStaff')}
                            name="administrativos"
                            type="number"
                            value={formData.administrativos ?? ''}
                            onChange={handleChange}
                            disabled={formData.esFallida}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label={t('newVisit.label.productionStaff')}
                            name="personalProduccion"
                            type="number"
                            value={formData.personalProduccion ?? ''}
                            onChange={handleChange}
                            disabled={formData.esFallida}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel disabled={formData.esFallida}>
                                {t('newVisit.label.safetyService')}
                            </InputLabel>
                            <Select
                                name="seguridadHigiene"
                                value={formData.seguridadHigiene ?? ''}
                                onChange={(event) => {
                                    setFormData((prev: any) => ({
                                        ...prev,
                                        seguridadHigiene: event.target.value === 'true'
                                    }));
                                }}
                                disabled={formData.esFallida}
                            >
                                <MenuItem value={'true'}>{t('newVisit.common.yes')}</MenuItem>
                                <MenuItem value={'false'}>{t('newVisit.common.no')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label={t('newVisit.label.professional')}
                            name="profesional"
                            value={formData.profesional}
                            onChange={handleChange}
                            disabled={formData.esFallida || formData.seguridadHigiene === false}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label={t('newVisit.label.license')}
                            name="matricula"
                            value={formData.matricula}
                            onChange={handleChange}
                            disabled={formData.esFallida || formData.seguridadHigiene === false}
                        />
                    </Grid>
                    {selectedVisita?.nombreTipoVisita === 'GENERICA' && (
                        <Grid item xs={12} sm={12}>
                            <TextField
                                select
                                fullWidth
                                label={t('newVisit.label.subVisitType')}
                                name="idSubTipoVisita"
                                value={formData.idSubTipoVisita ?? ''}
                                onChange={handleChange}
                                disabled={loadingTiposVisitas || formData.esFallida}
                            >
                                {loadingTiposVisitas ? (
                                    <MenuItem disabled></MenuItem>
                                ) : (
                                    subTypesVisitasOptions.map((option: any) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.nombre}
                                        </MenuItem>
                                    ))
                                )}
                            </TextField>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t('newVisit.label.observation')}
                            name="observacion"
                            value={formData.observacion}
                            onChange={handleChange}
                            disabled={false}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={0} sx={{ p: 2 }} justifyContent={'space-between'}>
                    <Grid
                        item
                        xs={12}
                        sm={5.8}
                        sx={{ p: 2, borderRadius: '5px', border: '1px solid #0000003b' }}
                    >
                        <canvas
                            ref={canvasPreventorRef}
                            width={300}
                            height={150}
                            style={{
                                width: '100%',
                                height: '150px',
                                borderBottom: '1px solid #000',
                                background: '#F6F6F6'
                            }}
                        />
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={1}
                        >
                            <Typography variant="body2">
                                {t('newVisit.label.preventorSignature')}
                            </Typography>
                            <Button
                                onClick={() => handleClearSignature('preventor')}
                                variant="outlined"
                            >
                                {t('newVisit.common.clear')}
                            </Button>
                        </Box>
                        {signatureErrors.firmaPreventor && (
                            <Typography variant="body2" color="error">
                                {t('newVisit.enqueue.signatureError')}
                            </Typography>
                        )}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={5.8}
                        sx={{ p: 2, borderRadius: '5px', border: '1px solid #0000003b' }}
                    >
                        <canvas
                            ref={canvasResponsableRef}
                            style={{
                                width: '100%',
                                height: '150px',
                                borderBottom: '1px solid #000',
                                background: '#F6F6F6'
                            }}
                        />
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={1}
                        >
                            <Typography variant="body2">
                                {t('newVisit.label.responsibleSignature')}
                            </Typography>
                            <Button
                                onClick={() => handleClearSignature('responsable')}
                                variant="outlined"
                            >
                                {t('newVisit.common.clear')}
                            </Button>
                        </Box>
                        {signatureErrors.firmaResponsable && (
                            <Typography variant="body2" color="error">
                                {t('newVisit.enqueue.signatureError')}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Box>

            {(selectedVisita?.nombreTipoVisita === 'VEROSIMILITUD' ||
                selectedVisita?.nombreTipoVisita === 'RGRL') && (
                <Box
                    component="div"
                    justifyContent="center"
                    sx={{ mt: 4, backgroundColor: 'white', p: 2 }}
                >
                    <Typography variant="h3">
                        <strong>{t('newVisit.aditionalData')}</strong>
                    </Typography>

                    <Grid container spacing={1} sx={{ p: 2 }}>
                    {selectedVisita?.nombreTipoVisita === 'VEROSIMILITUD' && (
                        <VerosimilitudSection
                            isVerosimilitudDraft={isVerosimilitudDraft}
                            isVerosimilitudPendienteSinRespuestas={isVerosimilitudPendienteSinRespuestas}
                            selectedVisita={selectedVisita}
                            shouldShowVerosimilitudRow={shouldShowVerosimilitudRow}
                            handleOpenVerosimilitud={handleOpenVerosimilitud}
                            hasRgrlPresentations={hasRgrlPresentations()}
                            loadingVerosimilitud={loadingVerosimilitud}
                            loadingRgrl={loadingPresentaciones}
                        />
                        )}


                        {selectedVisita?.nombreTipoVisita === 'RGRL' && (
                            <RGRLSection
                                rgrlPresentaciones={rgrlPresentaciones}
                                loadingPresentaciones={loadingPresentaciones}
                                allPresentationsCancelled={allPresentationsCancelled}
                                handleOpenDialogRGRL={handleOpenDialogRGRL}
                                handleCardClickRGRL={handleCardClickRGRL}
                                confirmCancelPresentation={confirmCancelPresentation}
                                handleEditRGRL={handleEditRGRL}
                            />
                        )}
                    </Grid>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, p: 2 }}>
                <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                    {t('buttons.cancel')}
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={confirmSubmit}
                    disabled={!isFormValid}
                >
                    {t('newVisit.common.send')}
                </Button>
            </Box>

            <DrawerVisitas
                open={openDrawer}
                setOpen={setOpenDrawer}
                actionType="Establecimiento"
                visita={selectedVisitaDetail}
            />
            <DialogNewRGRL
                open={openDialogRGRL}
                handleClose={handleCloseDialogRGRL}
                handleCardClick={() => {}}
                handleCardClickRGRL={handleCardClickRGRL}
            />
        </>
    );
}

export default EjecutionsVisitsIndex;
